import apiSauce from "apisauce";
import axiosRetry from "axios-retry";

import { IMS } from "@app/services/imsService";
import { store } from "@app/store";

import {
  RateLimitGate,
  RequestQueue,
  getOrCreate,
  runRateLimited,
} from "./requestQueue";

export const DEFAULT_API_CLIENT_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

const DEFAULT_TIMEOUT_MS = 60000;

/** apisauce methods that issue a request and therefore have to be queued. */
const HTTP_METHODS = [
  "get",
  "post",
  "put",
  "patch",
  "delete",
  "head",
  "link",
  "unlink",
];

/**
 * Get authentication headers for API requests (token from Adobe IMS only).
 * @returns {Promise<object>} Headers object with authentication information
 */
export const getAuthHeaders = async () => {
  const token = await IMS.getAccessToken();
  if (!token) {
    return {};
  }
  return {
    Authorization: `Bearer ${token}`,
  };
};

export const authRequestTransform = (apiClient) => async (request) => {
  const authHeaders = await getAuthHeaders();
  request.headers = {
    ...request.headers,
    ...authHeaders,
  };
  if (!authHeaders.Authorization) {
    console.log("No Authorization header found, deleting");
    delete request.headers.Authorization;
    delete request.headers.authorization;
  }
  apiClient.setHeaders(request.headers);
};

export const authResponseTransform = (apiClient) => async (response) => {
  if (response.status !== 401) {
    return;
  }
  const refreshed = await IMS.refreshAccessToken();
  const authHeaders = refreshed ? await getAuthHeaders() : {};
  apiClient.setHeaders({
    ...(apiClient.headers || {}),
    ...authHeaders,
  });
  if (!authHeaders.Authorization) {
    const returnPath =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : null;
    store.getState().clearIms();
    if (returnPath) {
      store.getState().setRedirectPath(returnPath);
    }
    await IMS.signIn(true, returnPath ?? undefined);
  }
};

/**
 * `429` responses are deliberately **not** retried by axios-retry: an
 * interceptor has no knowledge of the other in-flight requests, so retrying
 * there would defeat both the concurrency limit and the `Retry-After` window.
 * Rate limits are handled by the request queue installed below.
 */
const shouldRetryRequest = (error) =>
  error?.response?.status !== 429 &&
  error?.code !== "ERR_CANCELED" &&
  axiosRetry.isNetworkOrIdempotentRequestError(error);

/** Endpoint key a cool-down is tracked under, e.g. `/generate-3p`. */
const getEndpointKey = (url) => String(url ?? "").split("?")[0] || "/";

/**
 * All apisauce request methods take the axios config as their third argument
 * (`get(url, params, config)` / `post(url, data, config)`), which is where the
 * abort signal lives.
 */
const getSignal = (args) => args?.[1]?.signal;

/**
 * Routes every request of `apiClient` through a FIFO queue.
 *
 * - No more than `maxConcurrent` requests are in flight; the rest wait instead
 *   of being fired (and instead of failing fast and being retried in a burst).
 * - A `429` closes the cool-down gate of that endpoint for the `Retry-After`
 *   window: queued requests to it stay queued (without holding a slot, so
 *   other endpoints keep flowing) and the request is retried afterwards,
 *   through the queue, so retries also respect `maxConcurrent`.
 *
 * @param {object} apiClient - The apisauce client to wrap.
 * @param {object} options - `name`, `maxConcurrent` and `maxRateLimitRetries`.
 * @returns {object} - The same client, with a `requestQueue` property.
 */
const withRequestQueue = (
  apiClient,
  { name, maxConcurrent, maxRateLimitRetries },
) => {
  const queue = new RequestQueue({ name, maxConcurrent });
  const gates = new Map();

  const getGate = (url) =>
    getOrCreate(
      gates,
      getEndpointKey(url),
      (key) => new RateLimitGate({ name: `${name}${key}` }),
    );

  for (const method of HTTP_METHODS) {
    const request = apiClient[method];

    if (typeof request !== "function") {
      continue;
    }

    apiClient[method] = (url, ...args) =>
      runRateLimited(queue, getGate(url), () => request(url, ...args), {
        signal: getSignal(args),
        maxRetries: maxRateLimitRetries,
      });
  }

  apiClient.requestQueue = queue;

  return apiClient;
};

/**
 * Creates an apisauce client with IMS auth, retries and — when
 * `maxConcurrentRequests` is given — client side rate limiting.
 *
 * @param {string} baseURL - The base URL of the API.
 * @param {number} [maxConcurrentRequests] - Parallel requests allowed.
 * @param {number} [retries] - Transport retries for network/idempotent errors.
 * @param {object} [headers] - Default headers.
 * @param {number} [timeout] - Per request timeout in milliseconds.
 * @param {object} [options] - `maxRateLimitRetries` for `429` responses.
 * @returns {object} - The apisauce client.
 */
export const createApiClient = (
  baseURL,
  maxConcurrentRequests = null,
  retries = 1,
  headers = DEFAULT_API_CLIENT_HEADERS,
  timeout = DEFAULT_TIMEOUT_MS,
  { maxRateLimitRetries } = {},
) => {
  const apiClient = apiSauce.create({
    baseURL,
    headers,
    timeout,
  });

  apiClient.addAsyncRequestTransform(authRequestTransform(apiClient));
  apiClient.addAsyncResponseTransform(authResponseTransform(apiClient));

  if (retries > 0) {
    axiosRetry(apiClient.axiosInstance, {
      retries,
      retryCondition: shouldRetryRequest,
      // The default delay is `0`, which turns a hiccup into a burst.
      retryDelay: axiosRetry.exponentialDelay,
    });
  }

  if (maxConcurrentRequests) {
    withRequestQueue(apiClient, {
      name: baseURL,
      maxConcurrent: maxConcurrentRequests,
      maxRateLimitRetries,
    });
  }

  return apiClient;
};
